/**
 * Cash Drops — track daily end-of-day cash collection.
 * Expected = cash sales that day − petty cash taken from drawer.
 * Owner/admin only.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabase.js';
import { fetchPaged } from '../fetchPaged.js';
import {
  businessDayRangeIso,
  toBusinessDayYerevan,
} from '../lib/businessHealth.js';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { useCashierNames } from '../useCashierNames.js';
import { Callout, PageHeader } from '../ui/index.js';

const EXPENSE_I18N: Record<string, string> = {
  taxi: 'cashDrops.catTaxi',
  supplies: 'cashDrops.catSupplies',
  meals: 'cashDrops.catMeals',
  delivery: 'cashDrops.catDelivery',
  other: 'cashDrops.catOther',
};

type DropRow = {
  id: string;
  business_day: string;
  expected_amount: number;
  counted_amount: number | null;
  status: 'pending' | 'collected';
  collected_at: string | null;
  collected_by: string | null;
  note: string | null;
};

type ShiftExpenseLine = {
  id: string;
  amount: number;
  category: string;
  description: string | null;
  created_at: string;
  created_by: string | null;
};

type DayBreakdown = {
  cashSales: number;
  expensesTotal: number;
  expenses: ShiftExpenseLine[];
};

function fmtDay(ymd: string, locale: string) {
  const d = new Date(ymd + 'T12:00:00');
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDt(iso: string, locale: string) {
  return new Date(iso).toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtTime(iso: string, locale: string) {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function expectedFromBreakdown(b: DayBreakdown): number {
  return Math.max(0, b.cashSales - b.expensesTotal);
}

function authDisplayName(user: {
  email?: string | null;
  user_metadata?: Record<string, unknown>;
}): string | null {
  const meta = user.user_metadata ?? {};
  const fromMeta =
    (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
    (typeof meta.name === 'string' && meta.name.trim()) ||
    '';
  return fromMeta || user.email?.trim() || null;
}

const SHIFT_ID_CHUNK = 200;

async function fetchDayBreakdowns(
  clientId: string,
  fromYmd: string,
  toYmd: string,
): Promise<Map<string, DayBreakdown>> {
  if (!supabase) return new Map();
  const db = supabase;
  const { fromIso, toIso } = businessDayRangeIso(fromYmd, toYmd);

  type PayRow = { amount: number; orders: { paid_at: string | null } | null };
  const payments = (await fetchPaged((offset, limit) =>
    db
      .from('payments')
      .select('amount, order_id, orders!inner(status, paid_at, client_id)')
      .eq('client_id', clientId)
      .eq('status', 'paid')
      .eq('method', 'cash')
      .filter('orders.status', 'eq', 'paid')
      .filter('orders.paid_at', 'gte', fromIso)
      .filter('orders.paid_at', 'lte', toIso)
      .filter('orders.client_id', 'eq', clientId)
      .order('created_at', { ascending: true })
      .range(offset, limit),
  )) as unknown as PayRow[];

  type ShiftRow = { id: string; opened_at: string };
  const shifts = (await fetchPaged((offset, limit) =>
    db
      .from('shifts')
      .select('id, opened_at')
      .eq('client_id', clientId)
      .gte('opened_at', fromIso)
      .lte('opened_at', toIso)
      .order('opened_at', { ascending: true })
      .range(offset, limit),
  )) as ShiftRow[];

  const shiftOpenedAt = new Map(shifts.map((s) => [s.id, s.opened_at]));
  const shiftIds = shifts.map((s) => s.id);

  type ExpRow = {
    id: string;
    amount: number;
    category: string;
    description: string | null;
    created_at: string;
    created_by: string | null;
    shift_id: string;
  };
  const expenses: ExpRow[] = [];
  for (let i = 0; i < shiftIds.length; i += SHIFT_ID_CHUNK) {
    const chunk = shiftIds.slice(i, i + SHIFT_ID_CHUNK);
    const batch = (await fetchPaged((offset, limit) =>
      db
        .from('shift_expenses')
        .select('id, amount, category, description, created_at, created_by, shift_id')
        .eq('client_id', clientId)
        .in('shift_id', chunk)
        .order('created_at', { ascending: true })
        .range(offset, limit),
    )) as ExpRow[];
    expenses.push(...batch);
  }

  const byDay = new Map<string, DayBreakdown>();

  function ensureDay(day: string): DayBreakdown {
    let entry = byDay.get(day);
    if (!entry) {
      entry = { cashSales: 0, expensesTotal: 0, expenses: [] };
      byDay.set(day, entry);
    }
    return entry;
  }

  for (const p of payments) {
    const paidAt = p.orders?.paid_at;
    if (!paidAt) continue;
    const day = toBusinessDayYerevan(paidAt);
    ensureDay(day).cashSales += Number(p.amount);
  }

  for (const e of expenses) {
    const opened = shiftOpenedAt.get(e.shift_id);
    if (!opened) continue;
    const day = toBusinessDayYerevan(opened);
    const entry = ensureDay(day);
    const amount = Number(e.amount);
    entry.expensesTotal += amount;
    entry.expenses.push({
      id: e.id,
      amount,
      category: e.category,
      description: e.description,
      created_at: e.created_at,
      created_by: e.created_by,
    });
  }

  for (const entry of byDay.values()) {
    entry.expenses.sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  return byDay;
}

async function computeDailyExpected(
  clientId: string,
  fromYmd: string,
  toYmd: string,
): Promise<Map<string, number>> {
  const breakdowns = await fetchDayBreakdowns(clientId, fromYmd, toYmd);
  const expected = new Map<string, number>();
  for (const [day, b] of breakdowns) {
    const val = expectedFromBreakdown(b);
    if (val > 0 || b.cashSales > 0) expected.set(day, val);
  }
  return expected;
}

function breakdownRangeForRows(rows: DropRow[]): { fromYmd: string; toYmd: string } {
  const todayYmd = toBusinessDayYerevan(new Date().toISOString());
  if (rows.length === 0) {
    const from = new Date();
    from.setDate(from.getDate() - 60);
    return { fromYmd: toBusinessDayYerevan(from.toISOString()), toYmd: todayYmd };
  }
  const days = rows.map((r) => r.business_day).sort();
  const fromYmd = days[0]!;
  const last = days[days.length - 1]!;
  return { fromYmd, toYmd: last > todayYmd ? last : todayYmd };
}

export function CashDropsView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const expenseLabel = (cat: string) => {
    const key = EXPENSE_I18N[cat];
    return key ? t(key) : cat;
  };

  const [rows, setRows] = useState<DropRow[]>([]);
  const [breakdowns, setBreakdowns] = useState<Map<string, DayBreakdown>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sessionUser, setSessionUser] = useState<{ id: string; name: string | null } | null>(null);
  const cashierNames = useCashierNames(clientId);
  const [collectId, setCollectId] = useState<string | null>(null);
  const [countedInput, setCountedInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [saving, setSaving] = useState(false);

  const loadBreakdowns = useCallback(
    async (dropRows: DropRow[]) => {
      if (!clientId) return;
      const { fromYmd, toYmd } = breakdownRangeForRows(dropRows);
      const map = await fetchDayBreakdowns(clientId, fromYmd, toYmd);
      setBreakdowns(map);
    },
    [clientId],
  );

  const load = useCallback(async () => {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('cash_drops')
      .select('id, business_day, expected_amount, counted_amount, status, collected_at, collected_by, note')
      .eq('client_id', clientId)
      .order('business_day', { ascending: false })
      .limit(90);
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    const nextRows = (data ?? []) as DropRow[];
    setRows(nextRows);
    await loadBreakdowns(nextRows);
    setLoading(false);
  }, [clientId, loadBreakdowns]);

  const syncExpected = useCallback(async () => {
    if (!supabase || !clientId) return;
    setSyncing(true);
    setError(null);
    try {
      const now = new Date();
      const toYmd = toBusinessDayYerevan(now.toISOString());
      const from = new Date(now);
      from.setDate(from.getDate() - 60);
      const fromYmd = toBusinessDayYerevan(from.toISOString());

      const expected = await computeDailyExpected(clientId, fromYmd, toYmd);

      const { data: existingRows } = await supabase
        .from('cash_drops')
        .select('id, business_day, status')
        .eq('client_id', clientId)
        .gte('business_day', fromYmd);

      const existing = new Map((existingRows ?? []).map((r) => [r.business_day, r]));

      for (const [day, amount] of expected) {
        const cur = existing.get(day);
        if (cur?.status === 'collected') continue;
        if (cur) {
          await supabase.from('cash_drops').update({ expected_amount: amount }).eq('id', cur.id);
        } else if (amount > 0) {
          await supabase.from('cash_drops').insert({
            client_id: clientId,
            business_day: day,
            expected_amount: amount,
            status: 'pending',
          });
        }
      }
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('cashDrops.syncFailed'));
    } finally {
      setSyncing(false);
    }
  }, [clientId, load, t]);

  useEffect(() => {
    if (!supabase) return;
    void supabase.auth.getUser().then(({ data }) => {
      const user = data.user;
      if (!user) {
        setSessionUser(null);
        return;
      }
      setSessionUser({ id: user.id, name: authDisplayName(user) });
    });
  }, []);

  useEffect(() => {
    void load().then(() => void syncExpected());
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  function collectorName(userId: string | null): string | null {
    if (!userId) return null;
    const fromProfiles = cashierNames.byUserId[userId];
    if (fromProfiles) return fromProfiles;
    if (sessionUser?.id === userId && sessionUser.name) return sessionUser.name;
    return null;
  }

  const pending = useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
  const pendingTotal = pending.reduce((s, r) => s + Number(r.expected_amount), 0);

  async function markCollected() {
    if (!supabase || !collectId) return;
    const counted = Number(countedInput);
    if (!Number.isFinite(counted) || counted < 0) {
      setError(t('cashDrops.invalidCounted'));
      return;
    }
    setSaving(true);
    setError(null);
    const { data: user } = await supabase.auth.getUser();
    const { error: err } = await supabase
      .from('cash_drops')
      .update({
        status: 'collected',
        counted_amount: counted,
        collected_at: new Date().toISOString(),
        collected_by: user.user?.id ?? null,
        note: noteInput.trim() || null,
      })
      .eq('id', collectId);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setCollectId(null);
    setCountedInput('');
    setNoteInput('');
    await load();
  }

  const collecting = collectId ? rows.find((r) => r.id === collectId) : null;

  function toggleExpanded(row: DropRow) {
    const b = breakdowns.get(row.business_day);
    if (!b?.expenses.length) return;
    setExpandedId((cur) => (cur === row.id ? null : row.id));
  }

  return (
    <div className="sales-page ui-stack">
      <PageHeader
        title={t('cashDrops.title')}
        subtitle={t('cashDrops.subtitle')}
        actions={
          <button type="button" className="btn-ghost btn-sm" disabled={syncing} onClick={() => void syncExpected()}>
            {syncing ? t('cashDrops.syncing') : t('cashDrops.refreshExpected')}
          </button>
        }
      />

      {pending.length > 0 && (
        <div className="warning-banner" style={{ marginBottom: 16 }}>
          <strong>{t('cashDrops.waiting', { count: pending.length })}</strong>
          <span style={{ marginLeft: 8 }}>
            {t('cashDrops.expectedTotal', { amount: AMD(pendingTotal) })}
          </span>
        </div>
      )}

      {error && <Callout tone="danger">{error}</Callout>}
      {loading && (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      )}

      {!loading && (
        <div className="chart-card">
          <h2 style={{ margin: '0 0 12px', fontSize: 15 }}>{t('cashDrops.title')}</h2>
          <table className="table table-cards cash-drops-table">
            <thead>
              <tr>
                <th style={{ width: 36 }} aria-label={t('cashDrops.showExpenses')} />
                <th>{t('cashDrops.colDay')}</th>
                <th style={{ textAlign: 'right' }}>{t('cashDrops.colExpected')}</th>
                <th style={{ textAlign: 'right' }}>{t('cashDrops.colCounted')}</th>
                <th>{t('cashDrops.colStatus')}</th>
                <th>{t('cashDrops.colCollected')}</th>
                <th>{t('cashDrops.colCollectedBy')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const diff =
                  r.counted_amount != null ? Number(r.counted_amount) - Number(r.expected_amount) : null;
                const breakdown = breakdowns.get(r.business_day);
                const hasExpenses = (breakdown?.expenses.length ?? 0) > 0;
                const isExpanded = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={isExpanded ? 'cash-drop-row-expanded' : undefined}
                      onClick={hasExpenses ? () => toggleExpanded(r) : undefined}
                      style={hasExpenses ? { cursor: 'pointer' } : undefined}
                    >
                      <td className="cash-drop-expand-cell table-cards-hide">
                        {hasExpenses ? (
                          <button
                            type="button"
                            className="cash-drop-expand-btn"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? t('cashDrops.hideExpenses') : t('cashDrops.showExpenses')}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(r);
                            }}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        ) : null}
                      </td>
                      <td className="table-cards-title" style={{ fontWeight: 500 }}>
                        {fmtDay(r.business_day, locale)}
                        {hasExpenses && (
                          <span className="muted" style={{ display: 'block', fontSize: 11, fontWeight: 400 }}>
                            {t('cashDrops.expenseSummary', {
                              count: breakdown!.expenses.length,
                              amount: AMD(breakdown!.expensesTotal),
                            })}
                          </span>
                        )}
                      </td>
                      <td data-label={t('cashDrops.colExpected')} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {AMD(Number(r.expected_amount))}
                      </td>
                      <td data-label={t('cashDrops.colCounted')} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {r.counted_amount != null ? (
                          <span style={{ color: diff !== 0 ? 'var(--danger)' : undefined }}>
                            {AMD(Number(r.counted_amount))}
                            {diff !== 0 && diff != null && (
                              <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>
                                ({diff > 0 ? '+' : ''}
                                {AMD(diff)})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="muted">{t('common.emptyDash')}</span>
                        )}
                      </td>
                      <td data-label={t('cashDrops.colStatus')}>
                        <span className={`badge ${r.status === 'pending' ? 'badge-warning' : 'badge-success'}`}>
                          {r.status === 'pending' ? t('cashDrops.pending') : t('cashDrops.collected')}
                        </span>
                      </td>
                      <td data-label={t('cashDrops.colCollected')} className="muted" style={{ fontSize: 12 }}>
                        {r.collected_at ? fmtDt(r.collected_at, locale) : t('common.emptyDash')}
                        {r.note && <div style={{ fontSize: 11 }}>{r.note}</div>}
                      </td>
                      <td data-label={t('cashDrops.colCollectedBy')} style={{ fontWeight: 500 }}>
                        {collectorName(r.collected_by) ?? <span className="muted">{t('common.emptyDash')}</span>}
                      </td>
                      <td className="table-cards-actions" style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        {r.status === 'pending' && (
                          <button
                            type="button"
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setCollectId(r.id);
                              setCountedInput(String(Math.round(Number(r.expected_amount))));
                              setNoteInput('');
                            }}
                          >
                            {t('cashDrops.markCollected')}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && breakdown && (
                      <tr className="cash-drop-detail-row">
                        <td colSpan={8}>
                          <div className="cash-drop-accordion">
                            <div className="cash-drop-breakdown-summary">
                              <span>
                                {t('cashDrops.cashSales')} <strong>{AMD(breakdown.cashSales)}</strong>
                              </span>
                              <span className="muted">−</span>
                              <span>
                                {t('cashDrops.expenses')} <strong>{AMD(breakdown.expensesTotal)}</strong>
                              </span>
                              <span className="muted">=</span>
                              <span>
                                {t('cashDrops.expected')} <strong>{AMD(expectedFromBreakdown(breakdown))}</strong>
                              </span>
                            </div>
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>{t('cashDrops.colTime')}</th>
                                  <th>{t('cashDrops.colCategory')}</th>
                                  <th>{t('cashDrops.colDescription')}</th>
                                  <th>{t('cashDrops.colBy')}</th>
                                  <th style={{ textAlign: 'right' }}>{t('cashDrops.colAmount')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {breakdown.expenses.map((exp) => (
                                  <tr key={exp.id}>
                                    <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                      {fmtTime(exp.created_at, locale)}
                                    </td>
                                    <td>{expenseLabel(exp.category)}</td>
                                    <td>{exp.description?.trim() || t('common.emptyDash')}</td>
                                    <td className="muted" style={{ fontSize: 12 }}>
                                      {exp.created_by?.trim() || t('common.emptyDash')}
                                    </td>
                                    <td
                                      style={{
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: 'var(--danger)',
                                      }}
                                    >
                                      −{AMD(exp.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                    {t('cashDrops.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {collecting && (
        <div className="modal-overlay" onClick={() => setCollectId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2 style={{ marginTop: 0 }}>
              {t('cashDrops.collectTitle', { day: fmtDay(collecting.business_day, locale) })}
            </h2>
            <p className="muted">
              {t('cashDrops.expectedLabel', { amount: AMD(Number(collecting.expected_amount)) })}
            </p>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('cashDrops.countedLabel')}</span>
              <input
                className="input"
                type="number"
                min={0}
                value={countedInput}
                onChange={(e) => setCountedInput(e.target.value)}
                autoFocus
              />
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('cashDrops.noteLabel')}</span>
              <input className="input" value={noteInput} onChange={(e) => setNoteInput(e.target.value)} />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-ghost" onClick={() => setCollectId(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn-primary" disabled={saving} onClick={() => void markCollected()}>
                {saving ? t('common.saving') : t('cashDrops.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
